import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../hooks/useAuth.jsx';
import { Avatar } from './Avatar.jsx';
import { attendanceApi } from '../api/endpoints.js';
import { nowOffice, formatDate } from '../lib/time.js';

/**
 * Shared employee-page header. Shows avatar + greeting + current date,
 * an "Agent Online/Offline" pill driven by attendance.agent_online from
 * the server, and a Sign out button.
 *
 * The pill is hidden for non-employee roles since they don't have an
 * agent installed.
 *
 * Polls /attendance/current every 60s so the pill stays fresh without
 * requiring the parent page to plumb agent_online through.
 */
export function EmployeeHeader() {
    const { user, logout } = useAuth();

    // Tick once a minute so the date string re-renders if it crosses midnight
    // while the page is open.
    const [, setTick] = useState(0);
    useEffect(() => {
        const id = setInterval(() => setTick((t) => t + 1), 60 * 1000);
        return () => clearInterval(id);
    }, []);

    const current = useQuery({
        queryKey: ['attendance', 'current'],
        queryFn: () => attendanceApi.current(),
        refetchInterval: 60 * 1000,
        // Don't fire for non-employees — they have no agent state to track
        enabled: user?.role === 'employee',
    });

    const agentOnline = current.data?.agent_online === true;
    const showPill = user?.role === 'employee';

    return (
        <header
            className="flex items-center justify-between pb-3.5 mb-5"
            style={{ borderBottom: '0.5px solid var(--color-border-tertiary)' }}
        >
            <div className="flex items-center gap-3">
                <Avatar user={user} size="lg" />
                <div>
                    <p className="eyebrow m-0">{formatDate(nowOffice())}</p>
                    <p
                        className="text-lg font-medium"
                        style={{ marginTop: 2, color: 'var(--color-text-primary)' }}
                    >
                        Welcome back, {user?.name?.split(' ')[0] ?? ''}
                    </p>
                </div>
            </div>
            <div className="flex items-center gap-3">
                {showPill && <AgentPill online={agentOnline} loading={current.isLoading} />}
                <button onClick={logout} className="btn btn-ghost">Sign out</button>
            </div>
        </header>
    );
}

function AgentPill({ online, loading }) {
    const colour = online
        ? 'var(--color-status-complete)'    // green
        : 'var(--color-status-weekend)';    // warm gray
    const label = loading
        ? 'Checking…'
        : (online ? 'Agent Online' : 'Agent Offline');
    return (
        <span
            className="flex items-center"
            style={{ gap: 8, fontSize: 13, color: 'var(--color-text-secondary)' }}
            title={online
                ? 'The desktop agent has reported activity within the last 10 minutes.'
                : 'The desktop agent has not reported activity in the last 10 minutes.'}
        >
            <span style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: colour,
            }} />
            {label}
        </span>
    );
}
